// ══════════════════════════════════════════════════════════
// ENTITLEMENTS TYPES
//
// Funcionalidades Premium — resueltas desde el backend.
// El frontend NUNCA concede acceso por flag local.
//
// Feature keys alineados con el spec del bloque 40:
// managed_streaming, autodj, extra_storage, multiple_stations,
// advanced_cabina, podcasts, contests, analytics, custom_domain,
// featured_radio, advanced_notifications, custom_app
// ══════════════════════════════════════════════════════════

// ── FEATURE KEYS ──

export type EntitlementKey =
  | 'managed_streaming'
  | 'autodj'
  | 'extra_storage'
  | 'multiple_stations'
  | 'advanced_cabina'
  | 'podcasts'
  | 'contests'
  | 'analytics'
  | 'custom_domain'
  | 'featured_radio'
  | 'advanced_notifications'
  | 'custom_app';

// ── FEATURE DEFINITION ──

/** Una funcionalidad premium individual con su estado resuelto. */
export interface Entitlement {
  key: EntitlementKey;
  label: string;
  description: string;
  icon: string;
  /** Solo el backend confirma. FALSE por defecto. */
  enabled: boolean;
  /** Límite numérico si aplica (ej: storage_gb, max_stations). null = ilimitado o no aplica. */
  limit: number | null;
  /** Unidad del límite (ej: "GB", "estaciones", "locutores") */
  limitUnit: string | null;
}

/** Mapa de todas las funcionalidades premium. */
export type EntitlementsMap = Record<EntitlementKey, Entitlement>;

// ── BACKEND RESPONSE ──

export interface BackendEntitlementsResponse {
  radio_id: string;
  plan_name: string;
  plan_slug: string;
  subscription_status: string;
  entitlements: BackendEntitlementEntry[];
}

export interface BackendEntitlementEntry {
  key: EntitlementKey;
  enabled: boolean;
  limit: number | null;
  limit_unit: string | null;
}

// ── UNIT TYPES ──

export interface EntitlementLimit {
  current: number;
  max: number | null;
  unit: string;
}

// ── CONSTANTS ──

export const ENTITLEMENT_DEFINITIONS: Omit<Entitlement, 'enabled' | 'limit' | 'limitUnit'>[] = [
  {
    key: 'managed_streaming',
    label: 'Streaming Administrado RFW',
    description: 'Hosting de streaming gestionado por RadioFrecuenciaWeb. AutoDJ, almacenamiento y monitoreo incluidos.',
    icon: 'ri-cloud-line',
  },
  {
    key: 'autodj',
    label: 'AutoDJ',
    description: 'Programación automática 24/7 con biblioteca musical. Mantiene tu radio al aire sin interrupciones.',
    icon: 'ri-disc-line',
  },
  {
    key: 'extra_storage',
    label: 'Almacenamiento Extra',
    description: 'Espacio adicional para fotos, videos, música y archivos de tu radio.',
    icon: 'ri-hard-drive-2-line',
  },
  {
    key: 'multiple_stations',
    label: 'Múltiples Estaciones',
    description: 'Gestioná más de una estación de radio desde el mismo panel.',
    icon: 'ri-stack-line',
  },
  {
    key: 'advanced_cabina',
    label: 'Cabina Avanzada',
    description: 'Funciones premium de cabina: moderación IA, cola inteligente, métricas en tiempo real.',
    icon: 'ri-dashboard-3-line',
  },
  {
    key: 'podcasts',
    label: 'Podcasts',
    description: 'Creá, publicá y gestioná podcasts con episodios ilimitados.',
    icon: 'ri-headphone-line',
  },
  {
    key: 'contests',
    label: 'Concursos',
    description: 'Creá y gestioná concursos interactivos para tu audiencia.',
    icon: 'ri-trophy-line',
  },
  {
    key: 'analytics',
    label: 'Analítica Avanzada',
    description: 'Estadísticas detalladas de audiencia, engagement, geolocalización y tendencias.',
    icon: 'ri-bar-chart-2-line',
  },
  {
    key: 'custom_domain',
    label: 'Dominio Personalizado',
    description: 'Vinculá tu propio dominio a la página de tu radio.',
    icon: 'ri-global-line',
  },
  {
    key: 'featured_radio',
    label: 'Radio Destacada',
    description: 'Tu radio aparece en secciones destacadas del discovery y rankings.',
    icon: 'ri-star-line',
  },
  {
    key: 'advanced_notifications',
    label: 'Notificaciones Avanzadas',
    description: 'Notificaciones push, email marketing y campañas segmentadas para tus oyentes.',
    icon: 'ri-notification-3-line',
  },
  {
    key: 'custom_app',
    label: 'App Personalizada',
    description: 'App móvil y TV con el branding de tu radio. Sujeta a solicitud y aprobación.',
    icon: 'ri-smartphone-line',
  },
];

/** Helper: construir un mapa vacío de entitlements (todo deshabilitado). */
export function buildEmptyEntitlementsMap(): EntitlementsMap {
  const map = {} as EntitlementsMap;
  for (const def of ENTITLEMENT_DEFINITIONS) {
    map[def.key] = { ...def, enabled: false, limit: null, limitUnit: null };
  }
  return map;
}

/** Helper: fusionar definiciones base con datos del backend. */
export function buildEntitlementsMap(backendEntries: BackendEntitlementEntry[]): EntitlementsMap {
  const map = buildEmptyEntitlementsMap();
  for (const entry of backendEntries) {
    if (map[entry.key]) {
      map[entry.key] = {
        ...map[entry.key],
        enabled: entry.enabled,
        limit: entry.limit,
        limitUnit: entry.limit_unit,
      };
    }
  }
  return map;
}